import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { rxActions, RxActions } from '@rx-angular/state/actions';
import { ItineraryState } from '@vessel-itinerary/state/itinerary.state';
import { Bunker, InventorySupplyBody } from '@vessel-reports/port/state/port.types';
import { RobService } from '@vessel-reports/rob/state/rob.service';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { Detail, RobReportDetail } from '@vessel-reports/rob/state/rob.types';
import { PopupService } from 'app/core/services/popup.service';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

interface IRobAction {
  createNewReport: {
    body: any;
  };
  createNewReportSuccess: void;
  getDetailReport: {
    report_id: string;
  };
  getDetailReportSuccess: void;
  updateDetailReport: {
    body: any;
  };
  updateDetailReportSuccess: void;
  getPreviousReport: {
    vessel_id: string;
    type?: number;
  };
  getPreviousReportSuccess: void;
  deleteReport: {
    report_id: string;
    queryParams?: Params;
  };
  deleteReportSuccess: void;
  updateReport: {
    report_id: string;
    body: any;
  };
  updateReportSuccess: {
    report?: RobReportDetail;
  };
  submitReport: {
    report_id: string;
  };
  submitReportSuccess: void;
  createInventorySupply: {
    body: InventorySupplyBody;
  };
  createInventorySupplySuccess: void;
  updateInventorySupply: {
    id: string;
    body: InventorySupplyBody;
  };
  updateInventorySupplySuccess: void;
  deleteInventorySupply: {
    id: string;
  };
  deleteInventorySupplySuccess: void;
  getInventoriesSupply: {
    report_id: string;
  };
  createReportBunker: {
    body: any;
  };
  createReportBunkerSuccess: void;
  updateReportBunker: {
    report_id: string;
    body: Bunker;
  };
  updateReportBunkerSuccess: void;
  deleteReportBunker: {
    report_id: string;
  };
  deleteReportBunkerSuccess: void;
  getBunker: {
    bunker_id: string;
  };
  getAdvanceFilters: {
    key?: 'voyage_number' | 'date_report' | 'port_name' | 'year' | 'month';
    limit?: number;
    queryParams?: Params;
  };
  getAdvanceFiltersSuccess: {
    key?: 'voyage_number' | 'date_report' | 'port_name' | 'year' | 'month';
    types?: any[];
    voyages?: any[];
    date_report?: any;
    ports?: any[];
    years?: any[];
  };
  cleanState: any;
  getInventories: {
    queryParams?: Params;
  };
  getInventoriesSuccess: void;
  deleteDetail: {
    id: string;
  };
  deleteDetailSuccess: void;
  updateDetail: {
    id: string;
    body: Detail;
  };
  updateDetailSuccess: void;
  createDetail: {
    body: Detail;
  };
  createDetailSuccess: void;
  updateRobSequence: {
    body: any;
  };
  updateRobSequenceSuccess: void;
}

@Injectable({
  providedIn: 'root'
})
export class RobActions {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  actions: RxActions<IRobAction> = rxActions<IRobAction>();
  private readonly _service: RobService = inject(RobService);
  private readonly _state: RobState = inject(RobState);
  private readonly _itineraryState: ItineraryState = inject(ItineraryState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToCreateNewReport$();
    this._subscribeToGetDetailReport$();
    this._subscribeToUpdateDetailReport$();
    this._subscribeToGetPreviousReport$();
    this._subscribeToDeleteReport$();
    this._subscribeToUpdateReport$();
    this._subscribeToSubmitReport$();
    this._createInventorySupply$();
    this._updateInventorySupply$();
    this._deleteInventorySupply$();
    this._subscribeToGetInventoriesSupply$();
    this._subscribeToCreateReportBunker$();
    this._subscribeToDeleteReportBunker$();
    this._subscribeToUpdateReportBunker$();
    this._subscribeToGetBunker$();
    this._subscribeToGetAdvanceFilter$();
    this._subscribeToCleanState$();
    this._subscribeToGetInventories$();
    this._subscribeToDeleteDetail$();
    this._subscribeToUpdateDetail$();
    this._subscribeToCreateDetail$();
    this._subscribeToUpdateRobSequence$();
  }

  private _subscribeToCreateNewReport$() {
    this.actions.onCreateNewReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.createNewReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.createNewReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.rob_report_created_successfully')
        });

        this._state.set({
          detailReport: response?.data
        });
        this.actions.createNewReportSuccess();
      }
    );
  }

  private _subscribeToGetDetailReport$() {
    this.actions.onGetDetailReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getDetailReport>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._service.getDetailReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return this.actions.getDetailReportSuccess();
        }
        this._state.set({
          detailReport: response?.data
        });
        this.actions.getDetailReportSuccess();
      }
    );
  }

  private _subscribeToUpdateDetailReport$() {
    this.actions.onUpdateDetailReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateDetailReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.updateDetailReport(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.rob_detail_report_updated_successfully')
        });

        this.actions.updateDetailReportSuccess();
      }
    );
  }

  private _subscribeToGetPreviousReport$() {
    this.actions.onGetPreviousReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getPreviousReport>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.getPreviousReport(payload.vessel_id, payload.type).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._state.set({
          previousReport: response?.data
        });
        this.actions.getPreviousReportSuccess();
      }
    );
  }

  private _subscribeToDeleteReport$() {
    this.actions.onDeleteReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.deleteReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.deleteReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.rob_report_deleted_successfully')
        });
        this.actions.deleteReportSuccess();
      }
    );
  }

  private _subscribeToUpdateReport$() {
    this.actions.onUpdateReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.updateReport(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.rob_report_updated_successfully')
        });
        this._state.set({
          detailReport: response?.data
        });
        this.actions.updateReportSuccess({
          report: response?.data
        });
      }
    );
  }

  private _subscribeToSubmitReport$() {
    this.actions.onSubmitReport(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.submitReport>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.submitReport(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.rob_report_submitted_successfully')
        });
        this.actions.submitReportSuccess();
      }
    );
  }

  private _createInventorySupply$() {
    this.actions.onCreateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.createInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.createInventorySupply(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_created_successfully')
        });
        this.actions.createInventorySupplySuccess(response?.data);
      }
    );
  }

  private _updateInventorySupply$() {
    this.actions.onUpdateInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.updateInventorySupply(payload.id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_updated_successfully')
        });
        this.actions.updateInventorySupplySuccess(response?.data);
      }
    );
  }

  private _deleteInventorySupply$() {
    this.actions.onDeleteInventorySupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.deleteInventorySupply>;
        return payload$.pipe(
          tap(() => {
            this._popupService.loadingDialog();
          }),
          exhaustMap((payload) =>
            this._service.deleteInventorySupply(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.closeLoadingDialog();

        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.inventory_supply_deleted_successfully')
        });
        this.actions.deleteInventorySupplySuccess(response?.data);
      }
    );
  }

  private _subscribeToGetInventoriesSupply$() {
    this.actions.onGetInventoriesSupply(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getInventoriesSupply>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._service.getInventoriesSupply(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._itineraryState.set({
          vesselMovements: response?.data
        });
      }
    );
  }

  private _subscribeToCreateReportBunker$() {
    this.actions.onCreateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.createReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.createReportBunker(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_created_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.createReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToUpdateReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateReportBunker>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.updateReportBunker(payload.report_id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_updated_successfully')
        });
        this._state.set({
          detailBunker: response?.data
        });
        this.actions.updateReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToDeleteReportBunker$() {
    this.actions.onUpdateReportBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.deleteReportBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._service.deleteReportBunker(payload.report_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.report_bunker_deleted_successfully')
        });
        this.actions.deleteReportBunkerSuccess(response?.data);
      }
    );
  }

  private _subscribeToGetBunker$() {
    this.actions.onGetBunker(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getBunker>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._service.getBunker(payload.bunker_id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._state.set({
          detailBunker: response.data
        });
      }
    );
  }

  private _subscribeToGetAdvanceFilter$(): void {
    this.actions.onGetAdvanceFilters(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getAdvanceFilters>;

        return payload$.pipe(
          switchMap((payload) =>
            this._service.getAdvanceFilters(payload.key, payload.limit, payload.queryParams).pipe<APIResponse, APIResponse, any>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({ payload, response }))
            )
          )
        );
      },
      ({ payload, response }: any) => {
        if (payload?.key === 'year') {
          this._state.set({
            years: response?.data
          });
          this.actions.getAdvanceFiltersSuccess({ years: response?.data, key: payload?.key });
        }
      }
    );
  }

  private _subscribeToCleanState$() {
    this.actions.cleanState$.subscribe(() => {
      this._state.set({
        detailReport: null,
        previousReport: null,
        inventories: [],
        detailBunker: null,
        ports: [],
        selectedDetail: null,
        dateReport: null
      });
    });
  }

  private _subscribeToGetInventories$() {
    this.actions.onGetInventories(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.getInventories>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this._service.getInventories(payload.queryParams).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this._state.set({
          inventories: response?.data
        });
        this.actions.getInventoriesSuccess(response?.data);
      }
    );
  }

  private _subscribeToDeleteDetail$() {
    this.actions.onDeleteDetail(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.deleteDetail>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.deleteDetail(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.detail_deleted_successfully')
        });
        this.actions.deleteDetailSuccess(response?.data);
      }
    );
  }

  private _subscribeToUpdateDetail$() {
    this.actions.onUpdateDetail(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateDetail>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.updateDetail(payload.id, payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.detail_updated_successfully')
        });
        this.actions.updateDetailSuccess(response?.data);
      }
    );
  }

  private _subscribeToCreateDetail$() {
    this.actions.onCreateDetail(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.createDetail>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.createDetail(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.detail_created_successfully')
        });
        this.actions.createDetailSuccess(response?.data);
      }
    );
  }

  private _subscribeToUpdateRobSequence$() {
    this.actions.onUpdateRobSequence(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._service.updateInventorySequence>;
        return payload$.pipe(
          switchMap((payload) =>
            this._service.updateInventorySequence(payload.body).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.sequence_updated_successfully')
        });
        this.actions.updateRobSequenceSuccess(response?.data);
      }
    );
  }
}
